import { createReducer, createAction, combineReducers } from "@reduxjs/toolkit";
import {
  CustomerDisplayData,
  CustomerListData,
  EventData,
  EventType,
  FunnelDisplayConfig,
  LibItem,
  SessionData,
} from "../types";
import modalReducer from "./modalSlice";
import { UserPersonalData, UserPhoto } from "../types";
import {
  defaultEventFilters,
  EventListRequestParams,
} from "../forms/filters/event";
import {
  defaultTaskFilters,
  TaskListRequestParams,
} from "../forms/filters/task";
import { GroupData, Notification, Rights, TaskListItem } from "../services";
import { CustomerListFilters, defaultCustomerFilters } from "../forms/filters/customers";
import { defaultUserListFilters, UserFilters } from "../forms/filters/user";
import { saleFunnelTemplate, StatsFilters } from "../forms/filters/stats";
import { getDateRange } from "../utils";
import { RatingFilters, RatingFiltersUser } from "../forms/filters/rating";
import { defaultCurrencyId } from "../constants";

const selectedItem: string = "";
const defaultRights: Rights = {
  rang: 0,
  permissions: [],
  permitted_event_types: [],
  role_id: [],
  group_ids: []
}

type booleanAction = {
  type: string;
  payload: boolean;
};

type stringAction = {
  type: string;
  payload: string;
};

type numberAction = {
  type: string;
  payload: number;
};

type libItemAction = {
  type: string;
  payload: GroupData[]
}

const defaultUserPhoto: UserPhoto[] = [];
const defaultCustomerList: CustomerListData[] = [];
const eventTypeListDefault: EventType[] = []
const defaultEventData: EventData[] = [];
const defaultTaskData: TaskListItem[] = [];
const defAvailableGroupList: GroupData[] = [];
const defaultNotifyList: Notification[] = [];
const defaultStatsFilters: StatsFilters = {
   event_ids: saleFunnelTemplate
};
const defaultTemplateConfig: FunnelDisplayConfig = {
  template: "funnel",
  combinePractice: false
}
const defaultRatingFilters: RatingFiltersUser = {
  year: 2025,
  currency_id: defaultCurrencyId,
  order_value: "sales",
  order_dir: "desc"
}

export const actionNames = {
  menuHidden: "MENU_SIDED",
  selectItem: "SELECT_ITEM",
  saveSession: "SAVE_SESSION",
  setGroupsToSelect: "SET_GROUPS_TO_SELECT",
  currentUserProfile: "CURRENT_PROFILE",
  currentUserPhotos: "CURRENT_PHOTOS",
  customerListData: "CUSTOMER_LIST_DATA",
  currentCustomerDisplayData: "CUSTOMER_DISPLAY_DATA",
  eventData: "ALL_EVENT_DATA",
  taskData: "TASK_DATA",
  eventTypeList: "EVENT_TYPE_LIST",
  eventListRequestParams: "EVENT_REQUEST_PARAMS",
  taskListRequestParams: "TASK_REQUEST_PARAMS",
  customerListRequestParams: "CUSTOMER_REQUEST_PARAMS",
  userListRequestParams: "USER_REQUEST_PARAMS",
  rights: "RIGHTS",
  notifyOpen: "NOTIFY_OPEN",
  statsTemplateConfig: "STATS_TEMPLATE_CONFIG",
  statsFilters: "STATS_FILTERS",
  ratingFilters: "RATING_FILTERS",
  notificationList: "NOTIFICATION_LIST",
  hotNotify: "HOT_NOTIFY"
};

export const actions = {
  hideMenu: createAction<boolean>(actionNames.menuHidden),
  selectItem: createAction<string>(actionNames.selectItem),
  saveSession: createAction<SessionData>(actionNames.saveSession),
  setGroupsToSelect: createAction<LibItem[]>(actionNames.setGroupsToSelect),
  currentUserProfile: createAction<UserPersonalData>(
    actionNames.currentUserProfile
  ),
  currentUserPhotos: createAction<UserPhoto[]>(actionNames.currentUserPhotos),
  customerListData: createAction<CustomerListData[]>(
    actionNames.customerListData
  ),
  customerDisplayData: createAction<CustomerDisplayData>(
    actionNames.currentCustomerDisplayData
  ),
  eventData: createAction<EventData[]>(actionNames.eventData),
  eventTypes: createAction<EventType[]>(actionNames.eventTypeList),
  taskData: createAction<TaskListItem[]>(actionNames.taskData),
  eventRequestParams: createAction<EventListRequestParams>(
    actionNames.eventListRequestParams
  ),
  taskRequestParams: createAction<TaskListRequestParams>(
    actionNames.taskListRequestParams
  ),
  customerRequestParams: createAction<CustomerListFilters>(
    actionNames.customerListRequestParams
  ),
  userRequestParams: createAction<UserFilters>(
    actionNames.userListRequestParams
  ),
  rights: createAction<Rights>(actionNames.rights),
  notifyOpen: createAction<boolean>(actionNames.notifyOpen),
  statsTemplateConfig: createAction<FunnelDisplayConfig>(actionNames.statsTemplateConfig),
  statsFilters: createAction<StatsFilters>(actionNames.statsFilters),
  ratingFilters: createAction<RatingFiltersUser>(actionNames.ratingFilters),
  notificationList: createAction<Notification[]>(actionNames.notificationList),
  hotNotify: createAction<Notification | null>(actionNames.hotNotify)
};

const setHideMenu = (state = true, action: booleanAction) => {
  const newState =
    action.type === actionNames.menuHidden ? action.payload : state;
  return newState;
};

const itemSelector = (state = selectedItem, action: stringAction) => {
  const newState =
    action.type === actionNames.selectItem ? action.payload : state;
  return newState;
};

const saveSession = (
  state = null,
  action: { type: string; payload: SessionData | null }
) => {
  const newState =
    action.type === actionNames.saveSession ? action.payload : state;
  return newState;
};

const saveCurrentPersonal = (
  state = null,
  action: { type: string; payload: UserPersonalData | null }
) => {
  const newState =
    action.type === actionNames.currentUserProfile ? action.payload : state;
  return newState;
};

const saveCurrentPersonalPhoto = (
  state = defaultUserPhoto,
  action: { type: string; payload: UserPhoto[] }
) => {
  const newState =
    action.type === actionNames.currentUserPhotos ? action.payload : state;
  return newState;
};

const saveCustomerList = (
  state = defaultCustomerList,
  action: { type: string; payload: CustomerListData[] }
) => {
  const newState =
    action.type === actionNames.customerListData ? action.payload : state;
  return newState;
};

const saveCustomerDisplayData = (
  state = null,
  action: { type: string; payload: CustomerDisplayData }
) => {
  const newState =
    action.type === actionNames.currentCustomerDisplayData
      ? action.payload
      : state;
  return newState;
};

const saveEventData = (
  state = defaultEventData,
  action: { type: string; payload: EventData[] }
) => {
  const newState =
    action.type === actionNames.eventData ? action.payload : state;
  return newState;
};

const saveTaskData = (
    state = defaultTaskData,
    action: { type: string; payload: TaskListItem[] }
  ) => {
    const newState =
      action.type === actionNames.taskData ? action.payload : state;
    return newState;
  };

// Новый редьюсер для EventListRequestParams
const saveEventListRequestParams = (
  state = defaultEventFilters,
  action: { type: string; payload: EventListRequestParams }
) => {
  const newState =
    action.type === actionNames.eventListRequestParams ? action.payload : state;
  return newState;
};

const saveEventTypes = (
  state = eventTypeListDefault,
  action: { type: string; payload: EventType[] }
) => {
  const newState =
    action.type === actionNames.eventTypeList ? action.payload : state;
  return newState;
};


const saveTaskListRequestParams = (
  state = defaultTaskFilters,
  action: { type: string; payload: TaskListRequestParams }
) => {
  const newState =
    action.type === actionNames.taskListRequestParams ? action.payload : state;
  return newState;
};

const saveCustomerFilters = (
  state = defaultCustomerFilters,
  action: { type: string; payload: CustomerListFilters }
) => {
  const newState =
    action.type === actionNames.customerListRequestParams ? action.payload : state;
  return newState;
};

const saveUserFilters = (
  state = defaultUserListFilters,
  action: { type: string; payload: UserFilters }
) => {
  const newState =
    action.type === actionNames.userListRequestParams ? action.payload : state;
  return newState;
};

const saveRights = (state = defaultRights, action: {type: string, payload: Rights}) => {
  const newState =
    action.type === actionNames.rights ? action.payload : state;
  return newState;  
}

const setGroupsToSelect = (state = defAvailableGroupList, action: libItemAction) => {
  const newState =
    action.type === actionNames.setGroupsToSelect ? action.payload : state;
  return newState;    
}

const openNotify = (state = false, action: booleanAction) => {
  const newState =
    action.type === actionNames.notifyOpen ? action.payload : state;
  return newState;  
}

const setStatsTemplateConfig = (state = defaultTemplateConfig, action: { type: string; payload: FunnelDisplayConfig }) => {
  const newState =
    action.type === actionNames.statsTemplateConfig ? action.payload : state;
  return newState;  
}

const setStatsFilters = (state = defaultStatsFilters, action: {type: string; payload: StatsFilters}) => {
  const newState =
    action.type === actionNames.statsFilters ? action.payload : state;
  return newState;  
}

const setRatingFilters = (state = defaultRatingFilters, action: {type: string; payload: RatingFiltersUser }) => {
  const newState =
    action.type === actionNames.ratingFilters ? action.payload : state;
  return newState;  
}

const uplateNotifications = (state = defaultNotifyList, action: {type: string; payload: Notification[] }) => {
  const newState =
    action.type === actionNames.notificationList ? action.payload : state;
  return newState;  
}

const setHotNotify = (state = null, action: {type: string; payload: Notification | null }) => {
  const newState =
    action.type === actionNames.hotNotify ? action.payload : state;
  return newState;  
}

export const RootReducer = combineReducers({
  customerDetail: saveCustomerDisplayData,
  customers: saveCustomerList,
  currentPersonal: saveCurrentPersonal,
  currentUserPhoto: saveCurrentPersonalPhoto,
  eventData: saveEventData,
  eventTypes: saveEventTypes,
  taskData: saveTaskData,
  eventListRequestParams: saveEventListRequestParams, // Добавлен новый редьюсер
  taskListRequestParams: saveTaskListRequestParams,
  userListFilters: saveUserFilters,
  customerFilters: saveCustomerFilters,
  modal: modalReducer,
  menuHidden: setHideMenu,
  selectedItem: itemSelector,
  session: saveSession,
  rights: saveRights,
  notifyOpen: openNotify,
  statsTemplateConfig: setStatsTemplateConfig,
  statsFilters: setStatsFilters,
  ratingFilters: setRatingFilters,
  groupsToSelect: setGroupsToSelect,
  notifications: uplateNotifications,
  hotNotify: setHotNotify
});

export type RootState = ReturnType<typeof RootReducer>;
